import type { Log, PresentationTaskRunner, WinHighlightEffect } from "@slot/presentation";
import { multiplierBreakdown } from "./multiplierBreakdown";

/** The idle cycle's own timing (a title's values). */
export interface WinCycleTuning {
  /** The bright pause that leads every pass: after the first pass's wins, and between passes. Positive. */
  interCyclePauseSeconds: number;
}

/** What the cycle drives on the win highlight. */
export type WinCycleHighlight = Pick<WinHighlightEffect, "highlightWinByMask" | "undimNonWinners">;

/** A spin's win as the cycle records it: its union, its amount, its member lines. */
export interface WinCycleWin {
  positionMask: string;
  winAmount?: string;
  lines?: ReadonlyArray<{ positionMask: string; winAmount?: string; baseWinAmount?: string; multiplierApplied?: string }>;
}

/** One line as the cycle re-shows it: its own cells, its own amount, its breakdown when exact. */
interface CycleLine {
  mask: string;
  amount: string | undefined;
  breakdown: { base: string; multiplier: string } | undefined;
}

/**
 * The idle re-show of a spin's wins (decision 65; the Cocos template's `WinCyclePresenter`). The first pass presents a
 * win as a group (its union dimmed and lit, its total, its lines' strokes together); the cycle re-shows each LINE on
 * its own: its cells, its own stroke (the highlight's line hook with that line alone), its own amount, and "base ×
 * multiplier → amount" when the server multiplied it (exact only, `multiplierBreakdown`). It records each spin's wins
 * as they are presented and starts at the spin's end, fire-and-forget, so the round settles and goes idle while it
 * loops: the dimmed cells fade back (the highlight's `undimNonWinners`), a bright pause, every line in order, the fade
 * back again, and so on until `stop` (the next spin, a feature's exit). Unscoped (a slam never reaches it), silent (no
 * cue, no haptic), on engine time.
 */
export class WinCyclePresenter {
  private readonly highlight: WinCycleHighlight;
  private readonly runner: PresentationTaskRunner;
  private readonly tuning: WinCycleTuning;
  private readonly log: Log;
  private lines: CycleLine[] = [];
  private running = false;
  /** Bumped by every start and stop: a loop from before stops at its next step. */
  private generation = 0;

  public constructor(input: { highlight: WinCycleHighlight; runner: PresentationTaskRunner; tuning: WinCycleTuning; log: Log }) {
    this.highlight = input.highlight;
    this.runner = input.runner;
    this.tuning = input.tuning;
    this.log = input.log;
  }

  /** A presented win's lines, one entry per member line (a win without lines: its union and total). */
  public record(win: WinCycleWin): void {
    const lines = win.lines && win.lines.length > 0 ? win.lines : [{ positionMask: win.positionMask, winAmount: win.winAmount }];
    for (const line of lines) {
      this.lines.push({ mask: line.positionMask, amount: line.winAmount, breakdown: multiplierBreakdown([line], line.winAmount) });
    }
  }

  /** The loop starts (not awaited); a no-op with no line recorded, while one runs, or without a positive pause. */
  public start(): void {
    if (this.running || this.lines.length === 0) return;
    if (!(this.tuning.interCyclePauseSeconds > 0)) {
      this.log(`[WinCycle] not started: interCyclePauseSeconds must be positive (${this.tuning.interCyclePauseSeconds})`);
      return;
    }
    this.running = true;
    const generation = ++this.generation;
    this.log(`[WinCycle] start (${this.lines.length} line(s))`);
    void this.run(generation);
  }

  /** The loop stops and the spin's lines are forgotten. */
  public stop(): void {
    if (this.running) this.log("[WinCycle] stop");
    this.running = false;
    this.generation += 1;
    this.lines = [];
  }

  private async run(generation: number): Promise<void> {
    const current = (): boolean => this.running && generation === this.generation;
    await this.highlight.undimNonWinners();
    while (current()) {
      await this.runner.sleep(this.tuning.interCyclePauseSeconds);
      for (const line of this.lines) {
        if (!current()) return;
        await this.highlight.highlightWinByMask(line.mask, line.amount, undefined, undefined, line.breakdown);
      }
      if (!current()) return;
      await this.highlight.undimNonWinners();
    }
  }
}
